import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertTriangle, Info, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import CountrySelector from "./CountrySelector";
import ReportBugLink from "./ReportBugLink";
import { useLanguage } from "../contexts/LanguageContext";

const RegisterForm = ({ setUser, onSuccess }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [promoCodeValidation, setPromoCodeValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    country: "",
    currency: "",
    preferred_language: "fr",
    email: "",
    email_confirmation: "",
    password: "",
    password_confirmation: "",
    phone: "",
    phone_confirmation: "",
    communication_consent: false,
    sms_consent: false,
    promo_code: ""
  });

  // Password validation state
  const passwordValidation = {
    minLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /\d/.test(formData.password),
    matches: formData.password === formData.password_confirmation && formData.password_confirmation.length > 0
  };

  const isPasswordValid = passwordValidation.minLength && 
    passwordValidation.hasUppercase && 
    passwordValidation.hasLowercase && 
    passwordValidation.hasNumber;

  // Prevent paste on confirmation fields
  const preventPaste = (e) => {
    e.preventDefault();
    toast.error("Le copier-coller n'est pas autorisé pour ce champ");
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (field, value) => {
    const formatted = formatPhoneNumber(value);
    setFormData(prev => ({ ...prev, [field]: formatted }));
  };

  // Validate promo code
  const validatePromoCode = async (code) => {
    if (!code || code.trim().length < 3) {
      setPromoCodeValidation(null);
      return;
    }
    
    setValidatingPromo(true);
    try {
      const response = await axios.post(`${API}/partners/validate-code`, {
        promo_code: code.trim()
      });
      setPromoCodeValidation(response.data);
    } catch (error) {
      setPromoCodeValidation({
        valid: false,
        message: "Code promo invalide"
      });
    } finally {
      setValidatingPromo(false);
    }
  };

  // Debounce promo code validation
  const handlePromoCodeChange = (value) => {
    setFormData(prev => ({ ...prev, promo_code: value.toUpperCase() }));
    // Clear previous validation
    if (promoCodeValidation) {
      setPromoCodeValidation(null);
    }
  };

  const handlePromoCodeBlur = () => {
    if (formData.promo_code.trim()) {
      validatePromoCode(formData.promo_code);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug log
    console.log("Form submission - formData:", formData);

    // Validate all fields
    if (!formData.first_name.trim()) {
      toast.error("Le prénom est requis");
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error("Le nom de famille est requis");
      return;
    }
    if (!formData.country) {
      toast.error("Le pays de résidence est requis");
      return;
    }
    if (!formData.currency) {
      toast.error("La devise est requise");
      return;
    }
    if (!formData.email) {
      toast.error("Le courriel est requis");
      return;
    }
    if (formData.email.toLowerCase() !== formData.email_confirmation.toLowerCase()) {
      toast.error("Les courriels ne correspondent pas");
      return;
    }
    if (!isPasswordValid) {
      toast.error("Le mot de passe ne respecte pas les critères requis");
      return;
    }
    if (formData.password !== formData.password_confirmation) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    const phoneDigits = formData.phone.replace(/\D/g, "");
    const phoneConfDigits = formData.phone_confirmation.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      toast.error("Le numéro de téléphone doit contenir 10 chiffres");
      return;
    }
    if (phoneDigits !== phoneConfDigits) {
      toast.error("Les numéros de téléphone ne correspondent pas");
      return;
    }
    if (!formData.communication_consent) {
      toast.error("Vous devez accepter de recevoir les communications par courriel");
      return;
    }
    if (!formData.sms_consent) {
      toast.error("Vous devez accepter de recevoir les communications par SMS");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      localStorage.setItem("token", response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem("refreshToken", response.data.refresh_token);
      }
      
      toast.success(`Bienvenue ${response.data.user.first_name}! Votre numéro de client est: ${response.data.user.client_number}`);
      
      // Use onSuccess callback if provided, otherwise use setUser and navigate
      if (onSuccess) {
        onSuccess(response.data.user);
      } else if (setUser) {
        setUser(response.data.user);
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du compte");
    }
    setIsLoading(false);
  };

  const ValidationIcon = ({ isValid }) => (
    isValid ? 
      <CheckCircle className="h-4 w-4 text-green-500" /> : 
      <XCircle className="h-4 w-4 text-gray-300" />
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Name Section */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">{t('auth.firstName')} *</Label>
          <Input
            id="first_name"
            type="text"
            placeholder={t('auth.firstNamePlaceholder')}
            value={formData.first_name}
            onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
            required
            data-testid="register-first-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">{t('auth.lastName')} *</Label>
          <Input
            id="last_name"
            type="text"
            placeholder={t('auth.lastNamePlaceholder')}
            value={formData.last_name}
            onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
            required
            data-testid="register-last-name"
          />
        </div>
      </div>

      {/* Country Selection */}
      <CountrySelector
        value={formData.country}
        onChange={(country) => setFormData(prev => ({ ...prev, country }))}
        onCurrencyChange={(currency) => setFormData(prev => ({ ...prev, currency }))}
        error={null}
      />

      {/* Language Selection */}
      <div className="space-y-2">
        <Label htmlFor="preferred_language">{t('auth.communicationLanguage')} *</Label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="preferred_language"
              value="fr"
              checked={formData.preferred_language === "fr"}
              onChange={(e) => setFormData(prev => ({ ...prev, preferred_language: e.target.value }))}
              className="w-4 h-4 text-brand-600"
            />
            <span className="text-sm">{t('auth.french')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="preferred_language"
              value="en"
              checked={formData.preferred_language === "en"}
              onChange={(e) => setFormData(prev => ({ ...prev, preferred_language: e.target.value }))}
              className="w-4 h-4 text-brand-600"
            />
            <span className="text-sm">{t('auth.english')}</span>
          </label>
        </div>
        <p className="text-xs text-gray-500">{t('auth.communicationLanguageNote')}</p>
      </div>

      {/* Email Section */}
      {formData.country === "CA" && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 text-sm">
            <strong>Important:</strong> {t('auth.interacWarning')}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">{t('auth.loginEmail')} *</Label>
        <Input
          id="email"
          type="email"
          placeholder={t('auth.emailPlaceholder')}
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          required
          autoComplete="email"
          data-testid="register-email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email_confirmation">{t('auth.emailConfirmation')} *</Label>
        <Input
          id="email_confirmation"
          type="email"
          placeholder={t('auth.retypeEmail')}
          value={formData.email_confirmation}
          onChange={(e) => setFormData(prev => ({ ...prev, email_confirmation: e.target.value }))}
          onPaste={preventPaste}
          required
          autoComplete="off"
          data-testid="register-email-confirm"
          className={formData.email_confirmation && formData.email.toLowerCase() !== formData.email_confirmation.toLowerCase() ? "border-red-500" : ""}
        />
        {formData.email_confirmation && formData.email.toLowerCase() !== formData.email_confirmation.toLowerCase() && (
          <p className="text-xs text-red-500">{t('auth.emailsDoNotMatch')}</p>
        )}
      </div>

      {/* Password Section */}
      <div className="space-y-2">
        <Label htmlFor="password">{t('auth.password')} *</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            required
            autoComplete="new-password"
            data-testid="register-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        
        {/* Password Requirements */}
        <div className="grid grid-cols-2 gap-1 text-xs mt-2">
          <div className="flex items-center gap-1">
            <ValidationIcon isValid={passwordValidation.minLength} />
            <span className={passwordValidation.minLength ? "text-green-600" : "text-gray-500"}>{t('auth.minChars')}</span>
          </div>
          <div className="flex items-center gap-1">
            <ValidationIcon isValid={passwordValidation.hasUppercase} />
            <span className={passwordValidation.hasUppercase ? "text-green-600" : "text-gray-500"}>{t('auth.oneUppercase')}</span>
          </div>
          <div className="flex items-center gap-1">
            <ValidationIcon isValid={passwordValidation.hasLowercase} />
            <span className={passwordValidation.hasLowercase ? "text-green-600" : "text-gray-500"}>{t('auth.oneLowercase')}</span>
          </div>
          <div className="flex items-center gap-1">
            <ValidationIcon isValid={passwordValidation.hasNumber} />
            <span className={passwordValidation.hasNumber ? "text-green-600" : "text-gray-500"}>{t('auth.oneNumber')}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password_confirmation">{t('auth.passwordConfirmation')} *</Label>
        <div className="relative">
          <Input
            id="password_confirmation"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="••••••••"
            value={formData.password_confirmation}
            onChange={(e) => setFormData(prev => ({ ...prev, password_confirmation: e.target.value }))}
            onPaste={preventPaste}
            required
            autoComplete="new-password"
            data-testid="register-password-confirm"
            className={formData.password_confirmation && formData.password !== formData.password_confirmation ? "border-red-500" : ""}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {formData.password_confirmation && formData.password !== formData.password_confirmation && (
          <p className="text-xs text-red-500">{t('auth.passwordsDoNotMatch')}</p>
        )}
      </div>

      {/* Phone Section */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 text-sm">
          <strong>Important:</strong> Ce numéro de téléphone cellulaire sera utilisé pour vous envoyer des notifications lors de vos transferts de billets.
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <Label htmlFor="phone">{t('auth.phoneNumber')} *</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="(514) 555-1234"
          value={formData.phone}
          onChange={(e) => handlePhoneChange("phone", e.target.value)}
          required
          data-testid="register-phone"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone_confirmation">{t('auth.phoneConfirmation')} *</Label>
        <Input
          id="phone_confirmation"
          type="tel"
          placeholder="(514) 555-1234"
          value={formData.phone_confirmation}
          onChange={(e) => handlePhoneChange("phone_confirmation", e.target.value)}
          onPaste={preventPaste}
          required
          autoComplete="off"
          data-testid="register-phone-confirm"
          className={formData.phone_confirmation && formData.phone.replace(/\D/g, "") !== formData.phone_confirmation.replace(/\D/g, "") ? "border-red-500" : ""}
        />
        {formData.phone_confirmation && formData.phone.replace(/\D/g, "") !== formData.phone_confirmation.replace(/\D/g, "") && (
          <p className="text-xs text-red-500">{t('auth.phonesDoNotMatch')}</p>
        )}
      </div>

      {/* Promo Code - Optional */}
      <div className="space-y-2 pt-4 border-t">
        <Label htmlFor="promo_code" className="flex items-center gap-2">
          Code promo (optionnel)
          {validatingPromo && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
        </Label>
        <Input
          id="promo_code"
          type="text"
          placeholder="Ex: GROUPE-MTL"
          value={formData.promo_code}
          onChange={(e) => handlePromoCodeChange(e.target.value)}
          onBlur={handlePromoCodeBlur}
          className={`uppercase ${promoCodeValidation ? (promoCodeValidation.valid ? 'border-green-500' : 'border-red-500') : ''}`}
          data-testid="register-promo-code"
        />
        {promoCodeValidation && (
          <div className={`flex items-start gap-2 p-2 rounded text-sm ${promoCodeValidation.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {promoCodeValidation.valid ? (
              <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <p>{promoCodeValidation.message}</p>
              {promoCodeValidation.valid && promoCodeValidation.partner_name && (
                <p className="text-xs mt-1 text-green-600">
                  Parrainé par: {promoCodeValidation.partner_name}
                </p>
              )}
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500">
          Vous avez un code promo d'un groupe Facebook ou d'un partenaire? Entrez-le pour obtenir une réduction sur votre premier transfert!
        </p>
      </div>

      {/* Consent Checkboxes */}
      <div className="space-y-4 pt-2">
        <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border">
          <Checkbox
            id="communication_consent"
            checked={formData.communication_consent}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, communication_consent: checked }))}
            data-testid="register-email-consent"
            className="mt-0.5"
          />
          <label htmlFor="communication_consent" className="text-sm cursor-pointer">
            <span className="font-medium text-gray-900">{t('auth.communicationConsent')} *</span>
          </label>
        </div>

        <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border">
          <Checkbox
            id="sms_consent"
            checked={formData.sms_consent}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sms_consent: checked }))}
            data-testid="register-sms-consent"
            className="mt-0.5"
          />
          <label htmlFor="sms_consent" className="text-sm cursor-pointer">
            <span className="font-medium text-gray-900">{t('auth.smsConsent')} *</span>
          </label>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 font-semibold mt-6"
        disabled={isLoading}
        data-testid="register-submit"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {t('auth.creatingAccount')}
          </>
        ) : (
          t('auth.createMyAccount')
        )}
      </Button>

      <p className="text-xs text-center text-gray-500 mt-4">
        En créant un compte, vous acceptez nos{" "}
        <a href="/terms" className="text-[#2eb382] hover:underline">conditions d'utilisation</a>
        {" "}et notre{" "}
        <a href="/privacy" className="text-[#2eb382] hover:underline">politique de confidentialité</a>.
      </p>
      
      {/* Report bug link */}
      <div className="flex justify-center pt-4 mt-2">
        <ReportBugLink variant="muted" />
      </div>
    </form>
  );
};

export default RegisterForm;
